import React, { createContext, useContext, useMemo } from 'react';
import { Study } from '../StudyList';
import { useStudyReferenceFiles } from 'components/FileUploadDialog/hooks/useReferenceFiles/useStudyReferenceFiles';
import type { useStudyReferenceFilesReturn } from 'components/FileUploadDialog/hooks/useReferenceFiles/useReferenceFilesTypes';

export const StudyReferenceFilesContext = createContext<useStudyReferenceFilesReturn | null>(
  null
);

type Props = {
  reviewId: string;
  category: string;
  studies: Study[];
  pageSize: number;
  children?: React.ReactNode;
};

export const StudyReferenceFilesProvider = ({
  reviewId,
  category,
  studies,
  pageSize,
  children,
}: Props) => {
  const value = useStudyReferenceFiles(category, studies, pageSize, reviewId);

  return (
    <StudyReferenceFilesContext.Provider value={value}>
      {children}
    </StudyReferenceFilesContext.Provider>
  );
};

export const useStudyReferenceFilesProvider = () => {
  const context = useContext(StudyReferenceFilesContext);

  if (!context) {
    throw new Error(
      'useStudyReferenceFilesContext must be used within a <StudyReferenceFilesProvider>'
    );
  }

  return context;
};

// Like useFilesForReference, but tolerates being rendered without a
// StudyReferenceFilesProvider above it — returns an empty list instead of
// throwing. For consumers (e.g. the LibKey button, which also renders in the
// DE2 extraction list and in unit tests) that only want to react to a
// reference's files when a provider happens to be present.
export const useOptionalFilesForReference = (referenceId: number) => {
  const context = useContext(StudyReferenceFilesContext);
  const version = context?.getRefreshVersionForReference(referenceId) ?? 0;

  return useMemo(() => context?.getFilesForReference(referenceId) ?? [], [
    context,
    referenceId,
    version,
  ]);
};

// This hook only deals with a single reference's files
export const useFilesForReference = (referenceId: number) => {
  const {
    getFilesForReference,
    getRefreshVersionForReference,
    refreshReference,
  } = useStudyReferenceFilesProvider();

  const version = getRefreshVersionForReference(referenceId);

  // Memoize based on version - only recomputes when THIS reference changes
  return useMemo(
    () => ({
      version,
      files: getFilesForReference(referenceId),
      refresh: () => refreshReference(referenceId),
    }),
    // Only re-run when this reference's id or version changes
    // Note: no other dependencies should be added here
    [referenceId, version]
  );
};
